<template>
	<Col>
		<Row>
			<u><h3 @click="openChangeTemplateDialog" class="template-btn mr-2 cursor-pointer">Change template</h3></u>
			<u><h3 @click="openEditTemplateDialog" class="template-btn cursor-pointer">Edit template</h3></u>
		</Row>
		<template v-if="dynamicParams.length">
			<b><h3 class="mt-4">Dynamic content</h3></b>
			<h5 class="med">Map data into the dynamic fields</h5>
			<StringParamType
				v-for="(param, i) in dynamicParams"
				:param="param"
				:key="param.key + '_' + i"
				:readonly="false"
				v-bind="componentProps"
				forceTemplateValue
				:templateValue="localValue.params[param.key]"
				@update-input-field="updateInputField"
			/>
		</template>
	</Col>
</template>

<script>
import isEqual from 'lodash/isEqual';

import ParamTypeMixin from './mixins/ParamTypeMixin';
import StringParamType from '@/components/BlockEditor/BlockEditorParams/ParamTypes/StringParamType.vue';
import AppDialogManagerService from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';
import {
	setSelectedEmailTemplate,
	fetchEmailTemplates,
	fetchPublicEmailTemplates,
	setDefaultEmailTemplate,
	duplicateTemplate,
	duplicateAndSetAsDefault,
	fetchAllEmailTemplates,
} from '@/store/modules/transactionalEmailTemplate/actions';
import {
	useEmailTemplate,
	defaultEmailTemplate,
	selectedEmailTemplateId,
	usePublicEmailTemplate,
	useEmailTemplatesArr,
	firstPublicEmailTemplate,
} from '@/store/modules/transactionalEmailTemplate/getters';
import { mapGettersShorthand, mapActionsShorthand } from '@/store/utils/mappers';

const getDynamicParamKeysFromTemplateText = (templateText) => {
	const regexPattern = /\{\{(.+?)\}\}/g;
	const matches = templateText.match(regexPattern) || [];
	return matches.map((match) => match.replace(/{{|}}/g, '').trim());
};

export default {
	name: 'EmailTemplateParamType',
	mixins: [ParamTypeMixin],
	components: { StringParamType },
	beforeDestroy() {
		this.setSelectedEmailTemplate({ templateId: null });
		// This way mounted triggers components "watch" methods when creating a new action
	},
	async mounted() {
		await this.fetchAllEmailTemplates();
		let templateId = this.localValue?.template_id;
		const templateNotFound = !this.useEmailTemplate(templateId);
		const isLocalValueMissingInfo = !this.localValue;
		if (isLocalValueMissingInfo || templateNotFound) {
			if (!this.defaultEmailTemplate) {
				// No private email templates, duplicate the first public email template and mark as default
				await this.duplicateAndSetAsDefault({
					templateId: this.firstPublicEmailTemplate.id,
					isPublic: this.firstPublicEmailTemplate.is_public,
				});
			}
			templateId = this.defaultEmailTemplate.id;
		}
		await this.setSelectedEmailTemplate({ templateId });
	},
	methods: {
		...mapActionsShorthand({
			duplicateAndSetAsDefault,
			duplicateTemplate,
			setDefaultEmailTemplate,
			setSelectedEmailTemplate,
			fetchEmailTemplates,
			fetchPublicEmailTemplates,
			fetchAllEmailTemplates,
		}),
		updateInputField(newValue) {
			const paramVal = newValue.map.value;
			this.localValue = {
				...this.localValue,
				params: { ...this.localValue.params, [newValue.map.key]: paramVal },
			};
		},

		async openChangeTemplateDialog() {
			await AppDialogManagerService.openChangeEmailTemplateDialog({ templateIdInTabPreview: this.selectedTemplateId });
		},
		async openEditTemplateDialog() {
			if (this.isPublicEmailTemplate) {
				this.$toast.error('Public templates cannot be edited');
				return;
			}
			// Currently, if this.selectedTemplate is null (means selectedTemplateId has not loaded or email templates have not been fetched)
			// we open the default email template. Is this the preferred behavior or is it better to stall the dialog opening until the email templates are loaded?
			const template = this.selectedTemplate || this.defaultEmailTemplate;
			const editTemplateDialogRes = await AppDialogManagerService.openEditEmailTemplateDialog({
				templateId: template.id,
				templateTitle: template.title,
			});
			if (editTemplateDialogRes?.openChangeEmailTemplateDialog) {
				await AppDialogManagerService.openChangeEmailTemplateDialog({ templateIdInTabPreview: template.id });
			}
		},
		usePrivateOrPublicEmailTemplate(templateId) {
			return this.useEmailTemplate(templateId) || this.usePublicEmailTemplate(templateId);
		},
	},
	computed: {
		...mapGettersShorthand({
			firstPublicEmailTemplate,
			useEmailTemplatesArr,
			useEmailTemplate,
			defaultEmailTemplate,
			selectedEmailTemplateId,
			usePublicEmailTemplate,
		}),
		isPublicEmailTemplate() {
			return this.usePublicEmailTemplate(this.selectedTemplateId);
		},
		selectedTemplate() {
			return this.usePrivateOrPublicEmailTemplate(this.selectedTemplateId);
		},
		dynamicParams() {
			// Re-calculates every time params_map changes
			// Due to params_map being set via `updateInputField` method.
			return this.localValue?.params
				? Object.keys(this.localValue.params).map((key) => ({
						key,
						name: key,
						type: 'string',
						required: false,
				  }))
				: [];
		},
		componentProps() {
			return {
				...this.$attrs,
				node: this.node,
				action: this.action,
				service: this.service,
				acceptSingleValue: true,
				isFocused: this.isFocused,
				showDataMenu: this.showDataMenu,
			};
		},
		selectedTemplateId() {
			return this.selectedEmailTemplateId;
		},
		htmlBodyParams() {
			// sort makes sure that the `htmlBodyParams` watch will not be triggered when the order of the array changes
			return getDynamicParamKeysFromTemplateText(this.selectedTemplate?.html || '').toSorted();
		},
	},
	watch: {
		htmlBodyParams: {
			handler(newParams, oldParams) {
				const paramsChanged = !isEqual(newParams, oldParams);
				if (!paramsChanged) {
					return;
				}
				const params = Object.fromEntries(newParams.map((key) => [key, this.localValue?.params?.[key] || '']));
				this.localValue = { ...this.localValue, params };
			},
			deep: true,
		},
		selectedTemplateId: {
			handler(newTemplateId) {
				const template = this.usePrivateOrPublicEmailTemplate(newTemplateId);
				if (!template) {
					return;
				}
				const templateHtml = template?.html || '';
				this.localValue = { ...this.localValue, template_id: newTemplateId, template_html: templateHtml, is_public: template.is_public };
			},
		},
	},
};
</script>

<style lang="scss" scoped>
.tab-button {
	border-radius: 6px;
	height: 26px !important;
	font-size: 12px !important;
	padding: 0px 8px !important;
	border: 1px solid #e7eaf3;
	background: #f9fafc !important;
	&.selected {
		margin-left: -3px;
		color: var(--bold-border-color);
		background: var(--bg-color) !important;
		box-shadow: 0px 3px 6px var(--shadow-color) !important;
	}
}

.template-btn {
	color: #6a6be9;
}
</style>
